import { useMemo } from 'react';
import { ViewState } from '../types';

export interface CalendarEvent {
    id: string;
    date: Date;
    type: 'project_start' | 'project_end' | 'lead_task' | 'call_followup' | 'appointment' | 'service_visit' | 'project_task';
    title: string;
    subtitle?: string;
    referenceId: string;
    color: string;
    navTo?: ViewState;
    searchTerm: string;
    details?: any;
    onDelete?: () => void;
    sortTime: number; // Minutes since midnight for chronological sorting (0 = all-day)
}

/**
 * Parse a YYYY-MM-DD (or ISO) date string into a local Date
 * without timezone shifting.
 */
export function parseLocalDate(dateStr: string): Date {
    if (!dateStr) return new Date();
    const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
    return new Date(year, month - 1, day);
}

/** Format a local calendar date as YYYY-MM-DD (avoids UTC shift from toISOString). */
export function formatLocalDateString(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

export function todayLocalDateString(): string {
    return formatLocalDateString(new Date());
}

/* `estimates` se fue de aquí con los seguimientos automáticos: era la única
   fuente que las propuestas alimentaban, y no había otro evento de calendario
   que saliera de ellas. */
interface CalendarDataSources {
    projects: any[];
    leads: any[];
    appointments: any[];
    serviceVisits: any[];
}

export function useCalendarEvents({
    projects,
    leads,
    appointments,
    serviceVisits,
}: CalendarDataSources) {
    return useMemo(() => {
        const allEvents: CalendarEvent[] = [];

        // Appointments
        appointments.forEach(a => {
            // Parse "HH:MM" startTime to minutes since midnight
            const timeParts = (a.startTime || '00:00').split(':').map(Number);
            const sortTime = (timeParts[0] || 0) * 60 + (timeParts[1] || 0);
            allEvents.push({
                id: `appt-${a.id}`,
                date: parseLocalDate(a.date),
                type: 'appointment',
                title: `${a.startTime} - ${a.title}`,
                subtitle: a.clientName,
                referenceId: a.id,
                color: 'bg-navy-50 text-navy-800 border-navy-100',
                searchTerm: a.clientName,
                details: a,
                sortTime,
            });
        });

        // Projects (Start, End, & Tasks)
        projects.forEach(p => {
            if (p.status === 'Completed' || p.status === 'Cancelled') return;

            allEvents.push({
                id: `p-start-${p.id}`,
                date: parseLocalDate(p.startDate),
                type: 'project_start',
                title: `Start: ${p.name}`,
                subtitle: p.clientName,
                referenceId: p.id,
                color: 'bg-exito-bg text-exito border-exito/25',
                navTo: 'Projects',
                searchTerm: p.name,
                sortTime: 0,
            });
            allEvents.push({
                id: `p-end-${p.id}`,
                date: parseLocalDate(p.endDate),
                type: 'project_end',
                title: `Due: ${p.name}`,
                subtitle: p.clientName,
                referenceId: p.id,
                color: 'bg-peligro-bg text-peligro border-peligro/25',
                navTo: 'Projects',
                searchTerm: p.name,
                sortTime: 0,
            });
            p.tasks.forEach((t: any) => {
                if (t.dueDate && !t.isCompleted) {
                    allEvents.push({
                        id: `p-task-${t.id}`,
                        date: parseLocalDate(t.dueDate),
                        type: 'project_task',
                        title: `Task: ${t.description}`,
                        subtitle: p.name,
                        referenceId: t.id,
                        color: 'bg-acero-100 text-acero-700 border-acero-200',
                        navTo: 'Projects',
                        searchTerm: p.name,
                        sortTime: 0,
                    });
                }
            });
        });

        // Service Visits (Maintenance)
        serviceVisits.forEach(sv => {
            allEvents.push({
                id: `sv-${sv.id}`,
                date: parseLocalDate(sv.date),
                type: 'service_visit',
                title: `Service: ${sv.clientName}`,
                subtitle: sv.status,
                referenceId: sv.id,
                color: 'bg-brand-100 text-brand-700 border-brand-200',
                navTo: 'Services',
                searchTerm: sv.clientName,
                sortTime: 0,
            });
        });

        // Leads (Next Action)
        leads.forEach(l => {
            if (l.nextActionDate && l.status !== 'Won' && l.status !== 'Lost') {
                allEvents.push({
                    id: `lead-${l.id}`,
                    date: parseLocalDate(l.nextActionDate),
                    type: 'lead_task',
                    title: l.nextAction || 'Follow Up',
                    subtitle: l.name,
                    referenceId: l.id,
                    color: 'bg-aviso-bg text-aviso border-aviso/25',
                    navTo: 'Leads',
                    searchTerm: l.name,
                    sortTime: 0,
                });
            }
        });

        /* Aquí se pintaban los seguimientos automáticos programados («Follow up:
           Juan», el día que tocaba el siguiente correo). Se fueron con la
           automatización: sin proveedor de correo no hay envío que anunciar, y un
           evento en el calendario prometiendo uno es peor que no tener nada. El
           seguimiento se lleva a mano desde el tablero de Seguimientos. */

        allEvents.sort((a, b) => a.sortTime - b.sortTime);
        return allEvents;
    }, [projects, leads, appointments, serviceVisits]);
}

// ---------- Date math helpers ----------

export function getDaysInMonth(date: Date): (Date | null)[] {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfWeek = new Date(year, month, 1).getDay();

    const days: (Date | null)[] = [];
    for (let i = 0; i < firstDayOfWeek; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) days.push(new Date(year, month, i));
    return days;
}

export function isSameDay(d1: Date, d2: Date): boolean {
    return (
        d1.getDate() === d2.getDate() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getFullYear() === d2.getFullYear()
    );
}
